import { z } from "zod/v4";
import { baseDESchema, documentoAsociadoSchema, addDocumentoAsociadoValidation } from "./base-de-schema";

const localUbicacionSchema = z.object({
    direccion: z.string().min(1, "Dirección es requerida"),
    numeroCasa: z.number().min(0, "Número de casa es requerido").default(0),
    departamentoId: z.string().min(1, "Departamento es requerido"),
    departamentoNombre: z.string().min(1, "Departamento es requerido"),
    departamentoCodigo: z.string().min(1, "Departamento es requerido"),
    ciudadId: z.string().min(1, "Ciudad es requerida"),
    ciudadNombre: z.string().min(1, "Ciudad es requerida"),
    ciudadCodigo: z.string().min(1, "Ciudad es requerida"),
});

const vehiculoSchema = z.object({
    tipoVehiculo: z.string().min(1, "Tipo de vehículo es requerido"),
    marca: z.string().min(1, "Marca es requerida"),
    tipoIdentificacion: z.enum(["1", "2"]).default("2"), // 1=Número de ID, 2=Matrícula
    numeroId: z.string().optional(),
    numeroMatricula: z.string().optional(),
}).check((ctx) => {
    if (ctx.value.tipoIdentificacion === "1" && !ctx.value.numeroId)
        ctx.issues.push({ code: "custom", message: "Número de identificación es requerido", path: ["numeroId"], input: ctx.value.numeroId });
    if (ctx.value.tipoIdentificacion === "2" && !ctx.value.numeroMatricula)
        ctx.issues.push({ code: "custom", message: "Número de matrícula es requerido", path: ["numeroMatricula"], input: ctx.value.numeroMatricula });
});

const transportistaSchema = z.object({
    naturaleza: z.enum(["1", "2"]).default("1"), // 1=Contribuyente, 2=No contribuyente
    nombre: z.string().min(1, "Nombre es requerido"),
    ruc: z.string().optional(),
    dv: z.number().optional(),
    tipoDocumento: z.enum(["1", "2", "3", "4"]).optional(), // 1=Cédula PY, 2=Pasaporte, 3=Cédula extranjera, 4=Carnet residencia
    numeroDocumento: z.string().optional(),
    paisId: z.string().optional(), // ID del país (para el combobox)
    nacionalidad: z.string().length(3).optional(), // cNacTrans - código país 3 chars, sincronizado desde paisId
    nacionalidadDescripcion: z.string().min(4).max(30).optional(), // dDesNacTrans, sincronizado desde paisId
    domicilioFiscal: z.string().min(1, "Domicilio fiscal es requerido"), // dDomFisc
    numeroDocChofer: z.string().min(1, "Número de documento del chofer es requerido"),
    nombreChofer: z.string().min(1, "Nombre del chofer es requerido"),
    direccionChofer: z.string().min(1, "Dirección del chofer es requerida"), // dDirChof
}).check((ctx) => {
    if (ctx.value.naturaleza === "1" && !ctx.value.ruc)
        ctx.issues.push({ code: "custom", message: "RUC es requerido", path: ["ruc"], input: ctx.value.ruc });
    if (ctx.value.naturaleza === "1" && ctx.value.dv === undefined)
        ctx.issues.push({ code: "custom", message: "DV es requerido", path: ["dv"], input: ctx.value.dv });
    if (ctx.value.naturaleza === "2" && !ctx.value.tipoDocumento)
        ctx.issues.push({ code: "custom", message: "Tipo de documento es requerido", path: ["tipoDocumento"], input: ctx.value.tipoDocumento });
    if (ctx.value.naturaleza === "2" && !ctx.value.numeroDocumento)
        ctx.issues.push({ code: "custom", message: "Número de documento es requerido", path: ["numeroDocumento"], input: ctx.value.numeroDocumento });
    if (ctx.value.nacionalidad && !ctx.value.nacionalidadDescripcion)
        ctx.issues.push({ code: "custom", message: "Descripción de nacionalidad es requerida", path: ["nacionalidadDescripcion"], input: ctx.value.nacionalidadDescripcion });
});

// Schema de Nota de Remisión Electrónica (iTiDE = 7)
// No tiene sección de pago (condicionPago)
// Documento asociado es opcional (optional=true)
export const notaRemisionFormSchema = addDocumentoAsociadoValidation(
    baseDESchema.extend({
        ...documentoAsociadoSchema.shape,
        motivoEmisionNR: z.enum(["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14"]).default("1"),
        responsableEmision: z.enum(["1", "2", "3", "4", "5"]).default("1"),
        kmRecorrido: z.number().min(1, "Distancia debe ser mayor a 0").max(99999, "Distancia no puede superar 5 dígitos").default(1),
        tipoDocumentoAsociado: z.enum(["none", "1", "2"]).default("none"),
        // E10 Transporte
        tipoTransporte: z.enum(["1", "2"]).default("1"),    // iTipTrans: 1=Propio, 2=Tercero
        modalidadTransporte: z.enum(["1", "2", "3", "4"]).default("1"), // iModTrans
        nroVuelo: z.string().max(6).optional(), // dNroVuelo - obligatorio si modalidad = 3 (Aéreo)
        responsableFlete: z.enum(["1", "2", "3", "4", "5"]).default("1"), // iRespEmiNR
        fechaInicioTraslado: z.string().min(1, "Requerida"),
        fechaFinTraslado: z.string().min(1, "Requerida"),
        localSalida: localUbicacionSchema,
        localEntrega: localUbicacionSchema,
        vehiculo: vehiculoSchema,
        transportista: transportistaSchema,
    }).check((ctx) => {
        if (ctx.value.modalidadTransporte === "3" && !ctx.value.nroVuelo)
            ctx.issues.push({ code: "custom", message: "Requerido", path: ["nroVuelo"], input: ctx.value.nroVuelo });
        if (ctx.value.modalidadTransporte !== "3" && ctx.value.nroVuelo)
            ctx.issues.push({ code: "custom", message: "Solo se informa para modalidad Aérea", path: ["nroVuelo"], input: ctx.value.nroVuelo });
    }),
    true // optional: permite no informar documento asociado
);

export type NotaRemisionFormData = z.infer<typeof notaRemisionFormSchema>;
